import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Checkbox, Divider, FormControlLabel, FormGroup, Grid, Typography, } from '@mui/material';
import { useCallback, useContext, useEffect } from 'react';
import { ReducerContext, useManagerApprovalAndDeductEntitlement } from '../../hooks';
import { ToggleDisplayMode } from '../../types/toggleDisplayMode';
import { ManagerSetup } from '../ManagerSetup';
import { NewBookingContext } from '../NewBookingContext';
import { getEntitlementYearOverlapMessage, getPastDateSelectedWithApprovalRequiredMessage, isFutureDateInCurrentEntitlementYear, } from './add-absence.helper';
import { DurationDay } from './DurationDay';
export const AddAbsenceConfirmationPage = ({ duration, setInfoMessage, setErrorMessage, }) => {
    const { sendForApprovalDisplayMode, deductionDisplayMode } = useManagerApprovalAndDeductEntitlement();
    const { endInMorning, deductFromEntitlement, startInAfternoon, isManagerApprovalRequired, dateRange, updateState, } = useContext(NewBookingContext);
    const { userSettingsState } = useContext(ReducerContext);
    useEffect(() => {
        if (deductionDisplayMode === ToggleDisplayMode.Disabled &&
            !isFutureDateInCurrentEntitlementYear(dateRange.startDate.toDate(), dateRange.endDate.toDate(), userSettingsState)) {
            setInfoMessage((state) => [
                ...state,
                'Future absences must deduct from your entitlement.',
            ]);
        }
        if (sendForApprovalDisplayMode === ToggleDisplayMode.Disabled &&
            !isFutureDateInCurrentEntitlementYear(dateRange.startDate.toDate(), dateRange.endDate.toDate(), userSettingsState)) {
            setInfoMessage((state) => [
                ...state,
                'You have a manager set up to receive requests.',
            ]);
        }
        if (isManagerApprovalRequired && !deductFromEntitlement) {
            setErrorMessage('To send an approval request, your absence must deduct from your entitlement.');
            return;
        }
        if (isManagerApprovalRequired &&
            deductFromEntitlement &&
            !userSettingsState.managerEmail) {
            setErrorMessage('To send an approval request and deduct from your entitlement, you must have a manager setup');
            return;
        }
        const entitlementYearOverlapMessage = getEntitlementYearOverlapMessage(dateRange.startDate.toDate(), dateRange.endDate.toDate(), userSettingsState);
        if (entitlementYearOverlapMessage) {
            setErrorMessage(entitlementYearOverlapMessage);
            return;
        }
        const pastDateSelectedWithApprovalRequiredMessage = getPastDateSelectedWithApprovalRequiredMessage(dateRange.startDate.toDate(), dateRange.endDate.toDate(), userSettingsState, isManagerApprovalRequired);
        if (pastDateSelectedWithApprovalRequiredMessage) {
            setErrorMessage(pastDateSelectedWithApprovalRequiredMessage);
            return;
        }
        setErrorMessage('');
    }, [
        dateRange.endDate,
        dateRange.startDate,
        deductFromEntitlement,
        deductionDisplayMode,
        isManagerApprovalRequired,
        sendForApprovalDisplayMode,
        setErrorMessage,
        setInfoMessage,
        userSettingsState,
        userSettingsState.managerEmail,
    ]);
    const dateComponent = (title, dateFormatted) => {
        return (_jsxs(Box, { display: 'flex', gap: 1, children: [_jsx(Typography, { variant: 'subtitle1', children: title }), _jsx(Typography, { fontWeight: 'bold', variant: 'subtitle1', children: dateFormatted })] }));
    };
    const showAddManagerDetails = useCallback(() => isManagerApprovalRequired && !userSettingsState.managerEmail, [isManagerApprovalRequired, userSettingsState.managerEmail]);
    const options = useCallback(() => {
        return (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 12, children: (deductionDisplayMode === ToggleDisplayMode.Active ||
                        sendForApprovalDisplayMode === ToggleDisplayMode.Active) && (_jsxs(_Fragment, { children: [_jsx(Divider, { sx: { mb: 2 } }), _jsx(Typography, { variant: 'body2', children: "Please select the appropriate options." })] })) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(FormGroup, { children: [deductionDisplayMode !== ToggleDisplayMode.Hidden &&
                                deductionDisplayMode !== ToggleDisplayMode.Disabled && (_jsx(FormControlLabel, { defaultChecked: deductFromEntitlement, onChange: (_, checked) => updateState((state) => ({ ...state, deductFromEntitlement: checked })), control: _jsx(Checkbox, { "data-testid": 'deduct-absence-checkbox' }), label: _jsx(Typography, { variant: 'subtitle1', children: "Deduct this absence from my entitlement." }) })), sendForApprovalDisplayMode !== ToggleDisplayMode.Hidden &&
                                sendForApprovalDisplayMode !== ToggleDisplayMode.Disabled && (_jsx(FormControlLabel, { defaultChecked: isManagerApprovalRequired, onChange: (_, checked) => updateState((state) => ({
                                    ...state,
                                    isManagerApprovalRequired: checked,
                                })), control: _jsx(Checkbox, { "data-testid": 'manager-approval-checkbox' }), label: _jsx(Typography, { variant: 'subtitle1', children: "Send for approval to my Manager." }) }))] }) })] }));
    }, [
        deductFromEntitlement,
        deductionDisplayMode,
        isManagerApprovalRequired,
        sendForApprovalDisplayMode,
        updateState,
    ]);
    if (dateRange.endDate > dateRange.startDate) {
        const startDateFormatted = `${dateRange.startDate.format('D MMMM YYYY')} ${startInAfternoon ? '(PM only)' : ''}`;
        const endDateFormatted = `${dateRange.endDate.format('D MMMM YYYY')} ${endInMorning ? '(AM only)' : ''}`;
        return (_jsxs(Grid, { container: true, p: 2, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, display: 'flex', gap: 1, children: _jsx(DurationDay, { duration: duration }) }), _jsxs(Grid, { item: true, xs: 12, children: [dateComponent('Start date:', startDateFormatted), dateComponent('End date:', endDateFormatted)] }), options(), showAddManagerDetails() && (_jsx(Grid, { item: true, xs: 12, children: _jsx(ManagerSetup, { showAddManagerDetails: showAddManagerDetails() }) }))] }));
    }
    const period = startInAfternoon ? '(PM only)' : endInMorning ? '(AM only)' : '';
    const startDateFormatted = `${dateRange.startDate.format('D MMMM YYYY')} ${period}`;
    return (_jsxs(Grid, { container: true, p: 2, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, display: 'flex', gap: 1, children: _jsx(DurationDay, { duration: duration }) }), _jsx(Grid, { item: true, xs: 12, children: dateComponent('Start date:', startDateFormatted) }), options(), showAddManagerDetails() && (_jsx(Grid, { item: true, xs: 12, children: _jsx(ManagerSetup, { showAddManagerDetails: showAddManagerDetails() }) }))] }));
};
